import { type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck } from "@tabler/icons-react";
import { cn } from "../../../lib/utils";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import Loader from "../../../components/Loader";

type TranslatableItem = {
  uid: string;
  translations: Record<string, string | null>;
};

export function SpecialtyStep({
  specialties,
  selectedSpecialty,
  onSelectSpecialty,
  onSubmit,
  isAssisting,
}: {
  /** The rows to show, already searched and ordered by the host
   * (`useSpecialtySearch`) — this step renders, it does not filter. */
  specialties: readonly TranslatableItem[];
  selectedSpecialty: string;
  onSelectSpecialty: (value: string) => void;
  onSubmit: () => void;
  /** The model layer is still answering. "No specialty found" would be a lie
   * until it has: "MPR" has no string match and a real answer a second later. */
  isAssisting: boolean;
}) {
  const { t } = useTranslation();
  const selectedRowClass =
    "border-ds-border-selected bg-ds-secondary text-ds-primary";

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (selectedSpecialty.length > 0) onSubmit();
  };

  return (
    <form
      id="onboarding-form-specialty"
      className="flex w-full flex-col"
      onSubmit={handleSubmit}
    >
      <div className="flex w-full flex-col gap-2">
        {specialties.length === 0 ? (
          isAssisting ? (
            <div className="flex justify-center pt-8">
              <Loader />
            </div>
          ) : (
            <p className="pt-8 text-center font-dm-sans text-ui-body text-ds-slate-500">
              {t("No specialty found")}
            </p>
          )
        ) : (
          specialties.map((specialty) => {
            const isSelected = selectedSpecialty === specialty.uid;
            return (
              <button
                key={specialty.uid}
                type="button"
                aria-pressed={isSelected}
                className={cn(
                  "flex h-14 w-full shrink-0 items-center gap-2 rounded-2xl border px-4 text-left transition-colors",
                  isSelected ? selectedRowClass : "border-ds-black/6 bg-white"
                )}
                onClick={() => {
                  onSelectSpecialty(specialty.uid);
                }}
              >
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate font-dm-sans text-ui-body font-medium",
                    isSelected ? "text-ds-primary" : "text-primary-black"
                  )}
                >
                  {getItemTranslation(specialty)}
                </span>
                {/* The tick, and no chevron: the row does not lead anywhere on
                    its own, it is the answer. The second question is reached by
                    Continue like every other slide, and a chevron said otherwise
                    on the one row a practitioner had just chosen. */}
                {isSelected && (
                  <IconCheck
                    size={16}
                    stroke={2}
                    className="shrink-0 text-ds-primary"
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </form>
  );
}
